import { XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "@/utils/formatters";

export type CoverageAuditQuotaVariant = "upgrade" | "topup";

type Props = {
  open: boolean;
  variant: CoverageAuditQuotaVariant;
  auditLimit: number;
  auditsRefreshAt: string | null;
  onDismiss: () => void;
};

export default function CoverageAuditQuotaDialog({ open, variant, auditLimit, auditsRefreshAt, onDismiss }: Props) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onDismiss]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";

    return () => {
      main.style.overflowY = previousOverflow;
    };
  }, [open]);

  if (!open || !overlayRoot) {
    return null;
  }

  const isUpgrade = variant === "upgrade";
  const title = isUpgrade ? "You're out of free page audits" : "You're out of page audits this period";
  const body = isUpgrade ? (
    <>
      <p className="mt-2">
        You&apos;ve used all {auditLimit} free page audits. Upgrade to keep auditing pages and catch usability issues
        before your users do.
      </p>
      <p>Pro and Max add a monthly page-audit budget so you can keep watching the pages that matter.</p>
    </>
  ) : (
    <>
      <p className="mt-2">
        You&apos;ve used all {auditLimit} page audits for this billing period.
        {auditsRefreshAt ? ` Your quota resets on ${formatDate(auditsRefreshAt)}.` : ""}
      </p>
      <p>
        {auditsRefreshAt ? "Need more before then?" : "Need more page audits?"} Extra page audits are $1 each — contact
        us and we&apos;ll add them to your account.
      </p>
    </>
  );
  const ctaHref = isUpgrade ? "/subscription" : "/contact";
  const ctaLabel = isUpgrade ? "View plans" : "Contact us";

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coverage-audit-quota-title"
    >
      <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative my-auto w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div>
            <h2 id="coverage-audit-quota-title" className="text-lg font-semibold tracking-tight text-gray-900">
              {title}
            </h2>
            <div className="space-y-1 text-sm leading-relaxed text-gray-600">{body}</div>
          </div>
          <button
            type="button"
            className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
            onClick={onDismiss}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end">
          <button type="button" className="btn justify-center" onClick={onDismiss}>
            Not now
          </button>
          <Link href={ctaHref} className="btn btn-primary justify-center">
            {ctaLabel}
          </Link>
        </div>
      </div>
    </div>,
    overlayRoot,
  );
}
